import React, { useMemo } from 'react';
import AppConfig from '@config';

import { Image as ImageComponent } from '@components/Image';
import { CustomizedTypography } from '@components/Customized';

import { UfoImage } from './assets';

import useStyles from './useStyles';
import useSWR from 'swr';

import { fetcher } from '@api/rest';
import { CircularProgress, useMediaQuery, useTheme } from '@material-ui/core';
import { CapaQuotationChatRoomSharedFiles } from '@generated/graphql';
import useViewerDataContext from './context/useVeiwerDataContext';
import AnnotationDetailDrawer from './components/AnnotationDetailDrawer';
import AnnotationAddDrawer from './components/AnnotationAddDrawer';
import useViewerApiContext from './context/useViewerApiContext';
import { useLocation } from 'react-router-dom';

const DrawingFileViewer = ({
  fileId,
  fileName,
  viewerRef
}: CapaQuotationChatRoomSharedFiles & {
  viewerRef: React.MutableRefObject<HTMLIFrameElement | null>;
}) => {
  const location = useLocation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const isChat = new URLSearchParams(location.search).get('chatRoomId');
  const {
    selectedAnnotationId,
    draftAnnotationData,
    fileAnnotations
  } = useViewerDataContext();
  const {
    onCancleAddAnnotationMobile,
    onCloseAnnotationMobile
  } = useViewerApiContext();

  const classes = useStyles();

  const { data, error: fetchError } = useSWR(
    `${AppConfig.fileApiUrl}/cloud/${fileId}.scs`,
    fetcher
  );

  const selectedAnnotation = (fileAnnotations ?? []).find(
    (annotation) => annotation.annotationId === selectedAnnotationId
  );

  const MemoizedViewer = useMemo(
    () => (
      <iframe
        key={matches ? 'dims' : 'no-op'}
        ref={viewerRef}
        width="100%"
        height="100%"
        title="viewer"
        src={`${AppConfig.hoopsViewerUrl}/viewer.html?${
          matches ? 'dims=ext&' : ''
        }${isChat ? `anno${matches ? '=ext' : ''}` : ''}&lang=ko&modelId=${
          AppConfig.hoopsViewerAPI
        }/${fileId}.scs`}
      />
    ),
    [viewerRef, isChat, matches, fileId]
  );

  if (!fileId) return null;

  if (!data && fetchError) {
    return (
      <div className={classes['DrawingFile-Viewer-error']}>
        <ImageComponent src={UfoImage} />
        <CustomizedTypography
          align="center"
          fontWeight="500"
          color="textPrimary"
          variant="body1"
          gutterBottoms={6}
        >
          {fileName} 파일은
        </CustomizedTypography>
        <CustomizedTypography
          align="center"
          fontWeight="500"
          color="textPrimary"
          variant="body1"
          gutterBottoms={32}
        >
          현재 CAPA 뷰어로 볼 수 없습니다.
        </CustomizedTypography>
      </div>
    );
  }

  if (!data) return <CircularProgress />;

  return (
    <>
      {matches && isChat && (
        <AnnotationDetailDrawer
          annotation={selectedAnnotation}
          open={!!selectedAnnotationId}
          onClose={() => {
            if (!onCloseAnnotationMobile) return;
            onCloseAnnotationMobile();
          }}
        />
      )}
      {matches && onCancleAddAnnotationMobile && isChat && (
        <AnnotationAddDrawer
          open={!!draftAnnotationData}
          onClose={onCancleAddAnnotationMobile}
        />
      )}
      {MemoizedViewer}
    </>
  );
};

export default DrawingFileViewer;
